import { useMemo, useState } from 'react';
import { pathgen } from 'object-path-generator';
import { Controller, useForm } from 'react-hook-form';
import { useHistory } from 'react-router';
import { ConfirmationModal, Typography, Box, Card, EnhancedAutocomplete, Spinner } from 'seagull';
import { routes } from "../../../constants";
import { useRedirectToProposal } from "../../../hooks/useCreateAgreement";
import { genericConnector } from "../../../utils/react-hook-form.utils";
import { useProposalTemplates } from "../hooks/useProposalTemplates";
import { useCreateDraft } from "./hooks/useCreateDraft";
import s from "./SelectProposalTypeModal.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const SelectProposalTypeModalTestIds = pathgen('SelectProposalTypeModalTestIds');
export const SelectProposalTypeModal = _ref => {
  let {
    dismissModal,
    proposalType
  } = _ref;
  const [selectedProposalType, setSelectedProposalType] = useState(proposalType);
  const {
    openCreationModal
  } = useRedirectToProposal();
  const {
    templates,
    isLoading
  } = useProposalTemplates({
    refreshOnMount: true
  });
  const {
    createDraft,
    isInProgress
  } = useCreateDraft({
    onSuccess: dismissModal
  });
  const history = useHistory();
  const {
    control,
    handleSubmit
  } = useForm({
    mode: 'all',
    defaultValues: {
      proposalTemplateId: ''
    }
  });
  const proposalTypes = useMemo(() => [{
    title: 'Use a template',
    content: 'Pick a template from your library and tailor it to your needs.',
    onClick: () => {
      setSelectedProposalType('template');
    },
    testId: SelectProposalTypeModalTestIds.ProposalType.Template()
  }, {
    title: 'Start from scratch',
    content: 'Build your proposal from scratch for complete customization',
    onClick: () => {
      setSelectedProposalType('draft');
      openCreationModal();
      dismissModal();
    },
    testId: SelectProposalTypeModalTestIds.ProposalType.Draft()
  }], []);
  let primaryButton = '';
  let cancelButton = '';
  let title = 'Create proposal';

  // switch (selectedProposalType) {
  if (selectedProposalType === 'template') {
    primaryButton = 'Create proposal';
    cancelButton = 'Cancel';
    title = 'Choose template';
  }
  const createProposalFromTemplate = async _ref2 => {
    let {
      proposalTemplateId
    } = _ref2;
    if (proposalTemplateId) {
      createDraft(proposalTemplateId);
    }
  };
  const renderTypeSelection = () => /*#__PURE__*/_jsx(Box, {
    className: s.cardButtonsContainer,
    "data-testid": SelectProposalTypeModalTestIds.ProposalType.Root(),
    children: proposalTypes.map(type => /*#__PURE__*/_jsx(Card, {
      className: s.cardButton,
      as: "button",
      skin: "button",
      highlightOnHover: true,
      padding: "s",
      onClick: type.onClick,
      "data-testid": type.testId,
      children: /*#__PURE__*/_jsxs(Box, {
        className: s.cardButtonContent,
        children: [/*#__PURE__*/_jsx(Typography, {
          weight: "normal",
          variant: "paragraph1",
          children: type.title
        }), /*#__PURE__*/_jsx(Typography, {
          skin: "secondary",
          variant: "paragraph2",
          weight: "light",
          children: type.content
        })]
      })
    }, type.title))
  });
  const renderTemplateSelection = () => /*#__PURE__*/_jsx(Controller, {
    control: control,
    name: "proposalTemplateId",
    rules: {
      required: 'Please select a template'
    },
    render: renderProps => {
      const {
        showErrors,
        errorMsg
      } = genericConnector(renderProps);
      return /*#__PURE__*/_jsx(EnhancedAutocomplete, {
        options: templates !== null && templates !== void 0 ? templates : [],
        isLoading: isLoading,
        className: s.serviceTemplateSelector,
        keyField: "id",
        labelField: "title",
        textFieldProps: {
          label: 'Template name',
          placeholder: 'Choose a template to start from',
          error: showErrors,
          helperText: errorMsg
        },
        applyOptionChange: option => {
          const {
            field
          } = renderProps;
          field.onChange(option === null || option === void 0 ? void 0 : option.id);
        },
        newItemOption: {
          text: 'Create new template',
          onClick: () => {
            history.push(routes.proposalTemplateStudio);
            return Promise.resolve();
          }
        },
        emptySearchText: "No matches found. Try again or create a new template.",
        "data-testid": SelectProposalTypeModalTestIds.ServiceTemplateSelector()
      });
    }
  });
  return /*#__PURE__*/_jsx("form", {
    onSubmit: handleSubmit(createProposalFromTemplate),
    children: /*#__PURE__*/_jsx(ConfirmationModal, {
      showFooter: selectedProposalType === 'template',
      className: s.confirmationModal,
      contentClassName: s.confirmationModalContent,
      style: {
        padding: '0'
      },
      width: "500px",
      title: title,
      "data-testid": SelectProposalTypeModalTestIds.Root(),
      primaryButtonTestid: SelectProposalTypeModalTestIds.Buttons.CreateProposal(),
      textAlignCenter: true,
      onPrimaryButtonClick: handleSubmit(createProposalFromTemplate),
      onCloseButtonClick: dismissModal,
      cancelButtonDisabled: false,
      primaryButtonLabel: isInProgress ? /*#__PURE__*/_jsx(Spinner, {
        size: 40
      }) : primaryButton,
      primaryButtonDisabled: isInProgress,
      secondaryButtonLabel: cancelButton,
      secondaryButtonTestid: SelectProposalTypeModalTestIds.Buttons.Cancel(),
      onSecondaryButtonClick: dismissModal,
      content: selectedProposalType === 'template' ? renderTemplateSelection() : renderTypeSelection()
    })
  });
};